import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import axios from 'axios';
import { Badge, Spinner, Table, TextInput } from 'flowbite-react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import {
  HiOutlineArrowLeft,
  HiOutlineClipboardCopy,
  HiOutlineDownload,
  HiOutlineExternalLink,
  HiOutlineLockClosed,
} from 'react-icons/hi';

import Button from '../../components/Button';
import Notification from '../../components/Notification';
import ToastNotification from '../../components/ToastNotification';
import DefaultModal from '../../components/modals/DefaultModal';
import { ToastType } from '../../typesData/ToastNotificationTypes';
import {
  DEFAULT_PAGE_SIZE,
  RECIPIENTS_PAGE_SIZE_OPTIONS,
  buildLettersZip,
  collectRenderedLetters,
  correctTicketStatus,
  fetchRecipientsCsv,
  fetchTicket,
  fetchTicketRecipients,
  retryCoverLetter,
  updateTicketStatus,
  updateTicketTracking,
} from './api';
import { getBatchConflictInfo } from './Batches/helpers';
import {
  ACTION_RESULT_STATUS,
  actionForStatus,
  cancelledStepStates,
  copyToClipboard,
  downloadBlob,
  formatDateTime,
  formatRecipientAddress,
  getApiErrorMessage,
  recipientName,
  isBookTicket,
  ticketStepStates,
  historyEventLabel,
  heroStatusPillClass,
  isApprovalPendingStatus,
  isArchivedTicket,
  isItpPendingStatus,
  isOperatorCancelledTicket,
  isPrePrintStatus,
  isStatusHeldError,
  isUnknownStatusTicket,
  originOf,
  printerSendingRefetchInterval,
} from './helpers';
import { usePdfFirstPageThumbnail } from './pdfThumbnail';
import { PRINT_SHOP_ROOT_PATH, queuePathForOrigin } from './queues';
import { formatMessage, printShopStrings } from './strings';
import { BookOrderFacts, BookPrintFiles } from './BookTicketPanels';
import { BookApprovalPanel } from './BookApprovalPanel';
import { BookItpPanel } from './BookItpPanel';
import { BookCancelAction, BookCancellationSummary } from './BookCancelPanel';
import {
  CORRECTABLE_STATUSES,
  TicketAction,
  TicketDetail as TicketDetailType,
  TicketRecipient,
  TicketStatus,
} from './types';

// id forces <Notification> to remount per banner — the shared component
// hides itself via a mount-once timeout and never resets, so reusing the
// same element renders nothing after the first 5-second window.
// `batchId` (task_06) is set only for the ship mutation's distinct
// order_in_open_batch conflict — when present the banner renders as a link
// to that batch instead of the plain <Notification/>.
type Banner = {
  id: number;
  type: ToastType;
  message: string;
  batchId?: string;
};
let bannerSequence = 0;
const nextBanner = (
  type: ToastType,
  message: string,
  batchId?: string,
): Banner => ({
  id: (bannerSequence += 1),
  type,
  message,
  batchId,
});

const QUERY_KEY = 'print-shop-ticket';
const RECIPIENTS_QUERY_KEY = 'print-shop-ticket-recipients';

/** How many history events show before the "Show all" toggle (task 57). */
const HISTORY_COLLAPSED_COUNT = 5;

/**
 * The human-readable label for one history row (task 57). A status change reads
 * as a transition ("paid → accepted"); a same-status edit that carries a
 * `meta.type` (e.g. a `tracking_updated` correction from task 53) reads as its
 * meaning ("Shipment reference updated") instead of a bare "shipped → shipped".
 */


/**
 * A single print file rendered as a card in the hero body, exposing two
 * actions: a **Preview** (opens the PDF inline in a new browser tab) and a
 * **Download** (saves the file). PDFs render in-browser, so the print shop
 * can eyeball a cover or manuscript before pulling it to a machine (task 51).
 */
const PrintFileCard = ({
  badge,
  title,
  href,
  thumbnailUrl,
  renderThumbnailFromPdf,
  downloadTestId,
  downloadLabel,
  downloadAriaLabel,
  previewTestId,
  previewAriaLabel,
}: {
  badge: string;
  title?: string;
  href: string;
  thumbnailUrl?: string;
  /**
   * The manuscript has no server-generated thumbnail (unlike the book
   * cover), so when true this renders `href`'s first page to an image
   * client-side instead of using `thumbnailUrl`.
   */
  renderThumbnailFromPdf?: boolean;
  downloadTestId: string;
  downloadLabel: string;
  downloadAriaLabel: string;
  previewTestId: string;
  previewAriaLabel: string;
}) => {
  // A book carries a cover thumbnail; show it in place of the text badge so the
  // print file is scannable at a glance. If the image fails to load we fall
  // back to the accessible text badge (never a broken-image slot).
  const [staticThumbnailFailed, setStaticThumbnailFailed] = useState(false);
  const [pdfDisplayFailed, setPdfDisplayFailed] = useState(false);
  const { src: pdfThumbnailSrc, failed: pdfRenderFailed } =
    usePdfFirstPageThumbnail(renderThumbnailFromPdf ? href : undefined);

  const thumbnailSrc = renderThumbnailFromPdf ? pdfThumbnailSrc : thumbnailUrl;
  const thumbnailFailed = renderThumbnailFromPdf
    ? pdfRenderFailed || pdfDisplayFailed
    : staticThumbnailFailed;
  const isRenderingPdfThumbnail =
    Boolean(renderThumbnailFromPdf) && !pdfThumbnailSrc && !pdfRenderFailed;
  const showThumbnail = Boolean(thumbnailSrc) && !thumbnailFailed;

  return (
    <div className="flex flex-1 flex-col items-center rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-4 text-center">
      {showThumbnail ? (
        <img
          src={thumbnailSrc}
          alt=""
          data-testid={`${downloadTestId}-thumbnail`}
          onError={() =>
            renderThumbnailFromPdf
              ? setPdfDisplayFailed(true)
              : setStaticThumbnailFailed(true)
          }
          className="mb-2 aspect-[0.77] w-[120px] rounded-md border border-primary-100 object-cover"
        />
      ) : isRenderingPdfThumbnail ? (
        <div className="mb-2 aspect-[0.77] w-[120px] animate-pulse rounded-md border border-primary-100 bg-primary-100" />
      ) : (
        <div className="mb-2 flex aspect-[0.77] w-[120px] items-center justify-center rounded-md border border-primary-100 bg-white text-xs font-extrabold tracking-wide text-primary-700">
          {badge}
        </div>
      )}
      {title && (
        <span className="mb-3 text-sm font-semibold text-neutral-800">
          {title}
        </span>
      )}
      <div className="mt-auto flex flex-wrap items-center justify-center gap-2">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={previewTestId}
          aria-label={previewAriaLabel}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-1.5 text-xs font-bold text-primary-700 transition hover:bg-primary-50"
        >
          <HiOutlineExternalLink className="h-4 w-4" />
          {printShopStrings.detail.asset.preview}
        </a>
        <a
          href={href}
          download
          target="_blank"
          rel="noreferrer"
          data-testid={downloadTestId}
          aria-label={downloadAriaLabel}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-primary-700"
        >
          <HiOutlineDownload className="h-4 w-4" />
          {downloadLabel}
        </a>
      </div>
    </div>
  );
};

/**
 * One recipient's personalized cover letter in the recipients table. When the
 * letter is `rendered` it mirrors the PrintFileCard Preview/Download anchors
 * (the print shop pulls + prints it alongside the book); otherwise it reflects
 * the async render job — `pending` ("Rendering…") or `failed` — so an operator
 * never mistakes a not-yet-rendered letter for a missing one (tasks 2 + 3).
 */
const RecipientCoverLetterCell = ({
  recipient,
  onRetry,
  isRetrying,
}: {
  recipient: TicketRecipient;
  onRetry: (recipientId: string) => void;
  isRetrying: boolean;
}) => {
  const {
    coverLetterStatus,
    coverLetterPdfUrl,
    coverLetterRetrying,
    coverLetterAttempts,
    coverLetterFailureReason,
    coverLetterError,
  } = recipient;
  const name = recipientName(recipient);

  if (coverLetterStatus === 'rendered' && coverLetterPdfUrl) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={coverLetterPdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`recipient-cover-letter-preview-${recipient.id}`}
          aria-label={formatMessage(
            printShopStrings.detail.recipients.coverLetterPreviewAria,
            { name },
          )}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-2.5 py-1 text-xs font-bold text-primary-700 transition hover:bg-primary-50"
        >
          <HiOutlineExternalLink className="h-4 w-4" />
          {printShopStrings.detail.recipients.coverLetterPreview}
        </a>
        <a
          href={coverLetterPdfUrl}
          download
          target="_blank"
          rel="noreferrer"
          data-testid={`recipient-cover-letter-download-${recipient.id}`}
          aria-label={formatMessage(
            printShopStrings.detail.recipients.coverLetterDownloadAria,
            { name },
          )}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-primary-700"
        >
          <HiOutlineDownload className="h-4 w-4" />
          {printShopStrings.detail.recipients.coverLetterDownload}
        </a>
      </div>
    );
  }

  // Still auto-retrying (not yet terminal): a muted "Retrying…" label — never a
  // "failed" state mid-retry — optionally annotated with the attempt count.
  if (coverLetterRetrying) {
    return (
      <Badge
        color="gray"
        className="w-fit"
        data-testid={`recipient-cover-letter-retrying-${recipient.id}`}
      >
        {coverLetterAttempts !== undefined && coverLetterAttempts > 0
          ? formatMessage(
              printShopStrings.detail.recipients.coverLetterRetryingWithAttempt,
              { attempts: String(coverLetterAttempts) },
            )
          : printShopStrings.detail.recipients.coverLetterRetrying}
      </Badge>
    );
  }

  if (coverLetterStatus === 'failed') {
    // Terminal failure: prefer DS's friendly reason ("Render failed — …") and
    // expose the raw error on hover; fall back to the generic copy + hint when
    // an older DS deploy sends neither. The `coverLetterRetrying` branch above
    // already returns while still auto-retrying, so this branch — and the
    // manual Retry button below — is only reached once that has given up.
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          color="failure"
          className="w-fit"
          title={
            coverLetterError ||
            printShopStrings.detail.recipients.coverLetterFailedHint
          }
          data-testid={`recipient-cover-letter-failed-${recipient.id}`}
        >
          {coverLetterFailureReason
            ? formatMessage(
                printShopStrings.detail.recipients.coverLetterFailedReason,
                { reason: coverLetterFailureReason },
              )
            : printShopStrings.detail.recipients.coverLetterFailed}
        </Badge>
        <Button
          variant="OUTLINED"
          size="xs"
          disabled={isRetrying}
          onClick={() => onRetry(recipient.id)}
          data-testid={`recipient-cover-letter-retry-${recipient.id}`}
        >
          {isRetrying
            ? printShopStrings.detail.recipients.coverLetterRetryButtonPending
            : printShopStrings.detail.recipients.coverLetterRetryButton}
        </Button>
      </div>
    );
  }

  // Order has letters but this row's render is still in flight (pending or not
  // yet started) — show an active spinner so it reads as "generating", never a
  // bare em-dash the operator could mistake for a missing letter.
  return (
    <span
      data-testid={`recipient-cover-letter-generating-${recipient.id}`}
      className="inline-flex items-center gap-2 text-neutral-500"
    >
      <Spinner
        size="sm"
        aria-label={printShopStrings.detail.recipients.coverLetterGenerating}
      />{' '}
      {printShopStrings.detail.recipients.coverLetterGenerating}
    </span>
  );
};

/**
 * Print-shop ticket detail (V2 "action-forward" layout, task 42): a teal hero
 * with the ticket id, member, status, copy-id and the single allowed status
 * action, plus a horizontal status stepper; below it the print-file cards
 * (book cover + manuscript on separate machines, task 41), a compact payment +
 * history card (collapsed to the most recent events with a "Show all" toggle,
 * task 57), and a full-width recipients card (the mailing set only — the
 * excluded/"No address" view was removed in task 56 — paginated, with
 * member-corrected badges and the recipients CSV). Ship still encourages a
 * shipment reference, asking "Ship without a reference?" before accepting a
 * blank one.
 */
export default function TicketDetail() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<Banner | null>(null);
  const [pendingAction, setPendingAction] = useState<TicketAction | null>(null);
  const [trackingRef, setTrackingRef] = useState('');
  const [confirmEmptyShip, setConfirmEmptyShip] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  // Whether the (collapsed-by-default) status history shows every event (task 57).
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientPageSize, setRecipientPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Inline "Edit tracking" correction control (task 53).
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [trackingDraft, setTrackingDraft] = useState('');
  // "Edit status" correction control (task 60): override the status to fix a
  // mistake. The dialog holds the chosen target + an optional reason note.
  const [isCorrectingStatus, setIsCorrectingStatus] = useState(false);
  const [correctionStatus, setCorrectionStatus] = useState<TicketStatus | ''>(
    '',
  );
  const [correctionReason, setCorrectionReason] = useState('');

  const {
    data: ticket,
    isLoading,
    error,
  } = useQuery<TicketDetailType>(
    [QUERY_KEY, orderId],
    () => fetchTicket(orderId),
    {
      enabled: orderId !== '',
      refetchOnWindowFocus: false,
      // Right after an approval (or the ITP marked ready) the printer e-mail
      // is queued: poll briefly so "Sending…" turns into "Sent" without a
      // reload.
      refetchInterval: (data) => printerSendingRefetchInterval(data),
    },
  );

  const {
    data: recipientsPage,
    isLoading: recipientsLoading,
    isError: recipientsError,
  } = useQuery(
    [RECIPIENTS_QUERY_KEY, orderId, recipientPage, recipientPageSize],
    () =>
      // ABO only prints the mailing set, so we always request it (task 56). The
      // DS endpoint still accepts `skipped`; ABO just never asks for that view.
      fetchTicketRecipients(orderId, {
        skipped: false,
        page: recipientPage,
        limit: recipientPageSize,
      }),
    { enabled: orderId !== '' && Boolean(ticket), keepPreviousData: true },
  );

  const isNotFound =
    axios.isAxiosError(error) && error.response?.status === 404;

  const statusMutation = useMutation(
    (action: TicketAction) =>
      updateTicketStatus(orderId, { action, trackingRef }),
    {
      onSuccess: (updated) => {
        // Refetch rather than trusting the mutation payload into the cache.
        // (The status endpoint now returns the same shape as GET tickets/:id,
        // but the refetch keeps the page resilient to any future shape drift —
        // the original blank-page regression — and is pinned by a test.)
        queryClient.invalidateQueries([QUERY_KEY, orderId]);
        queryClient.invalidateQueries('print-shop-tickets');
        // The status moved between tabs, so the queue tab badges are now stale.
        queryClient.invalidateQueries('print-shop-ticket-counts');
        const message = formatMessage(
          printShopStrings.detail.actions.success,
          {
            orderId,
            status:
              printShopStrings.statusLabels[updated.status] ?? updated.status,
          },
        );
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: (mutationError, action) => {
        // task_06: the ship action alone can 409 with the distinct
        // order_in_open_batch shape (the order already sits in a different
        // open batch) — check that FIRST, before the generic fallback below,
        // and only for 'ship'. Every other action/error shape falls straight
        // through to the unchanged generic handling.
        const conflict =
          action === 'ship' ? getBatchConflictInfo(mutationError) : null;
        const message = conflict
          ? formatMessage(printShopStrings.detail.actions.orderInOpenBatch, {
              orderId,
            })
          : getApiErrorMessage(
              mutationError,
              formatMessage(printShopStrings.detail.actions.error, {
                orderId,
              }),
            );
        setBanner(nextBanner('FAILURE', message, conflict?.batchId));
        ToastNotification({ type: 'failure', message });
        queryClient.invalidateQueries([QUERY_KEY, orderId]);
      },
      onSettled: () => {
        setPendingAction(null);
        setTrackingRef('');
        setConfirmEmptyShip(false);
      },
    },
  );

  const csvMutation = useMutation(() => fetchRecipientsCsv(orderId), {
    onSuccess: (blob) => {
      downloadBlob(blob, `mod-recipients-${orderId}.csv`);
    },
    onError: () => {
      const message = printShopStrings.detail.recipients.downloadCsvError;
      setBanner(nextBanner('FAILURE', message));
      ToastNotification({ type: 'failure', message });
    },
  });

  // "Download all letters" (task 4): gather every rendered letter across all
  // recipient pages, zip them in the browser (no server archiver dep), and save
  // one file. The result reports how many of the mailing's letters were ready.
  const lettersMutation = useMutation(
    async () => {
      const letters = await collectRenderedLetters(orderId);
      const total = ticket?.quantity ?? letters.length;
      if (letters.length === 0) {
        return { downloaded: 0, total };
      }
      const zip = await buildLettersZip(letters);
      downloadBlob(zip, `mod-cover-letters-${orderId}.zip`);
      return { downloaded: letters.length, total };
    },
    {
      onSuccess: ({ downloaded, total }) => {
        if (downloaded === 0) {
          const message =
            printShopStrings.detail.recipients.downloadAllLettersEmpty;
          setBanner(nextBanner('OTHER', message));
          ToastNotification({ type: 'failure', message });
          return;
        }
        const pending = Math.max(0, total - downloaded);
        const message =
          pending > 0
            ? formatMessage(
                printShopStrings.detail.recipients.downloadAllLettersPartial,
                {
                  count: String(downloaded),
                  total: String(total),
                  pending: String(pending),
                },
              )
            : formatMessage(
                printShopStrings.detail.recipients.downloadAllLettersSuccess,
                { count: String(downloaded) },
              );
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: () => {
        const message =
          printShopStrings.detail.recipients.downloadAllLettersError;
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  // Tracking correction (task 53): a field-only update, NOT a status
  // transition. On success we refetch the ticket so the corrected value and
  // the new `tracking_updated` history entry both appear.
  const trackingMutation = useMutation(
    (nextTrackingRef: string) =>
      updateTicketTracking(orderId, nextTrackingRef),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, orderId]);
        queryClient.invalidateQueries('print-shop-tickets');
        setIsEditingTracking(false);
        const message = printShopStrings.detail.shipping.trackingUpdated;
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: (mutationError) => {
        const message = getApiErrorMessage(
          mutationError,
          printShopStrings.detail.shipping.trackingUpdateError,
        );
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  // Manual cover-letter retry (mod-cover-letter-manual-retry): row-level
  // feedback only on success (toast, no page banner) — the row re-renders
  // from whatever state the invalidated recipients query returns next
  // (pending, retrying, rendered, or failed again). A failure IS a page
  // banner + toast, mirroring `trackingMutation`'s error handling, since an
  // operator may miss a toast-only failure.
  const retryCoverLetterMutation = useMutation(
    (recipientId: string) => retryCoverLetter(orderId, recipientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, orderId]);
        queryClient.invalidateQueries([
          RECIPIENTS_QUERY_KEY,
          orderId,
          recipientPage,
          recipientPageSize,
        ]);
        queryClient.invalidateQueries('print-shop-tickets');
        const message =
          printShopStrings.detail.recipients.coverLetterRetryTriggered;
        ToastNotification({ type: 'success', message });
      },
      onError: (mutationError) => {
        const message = getApiErrorMessage(
          mutationError,
          printShopStrings.detail.recipients.coverLetterRetryError,
        );
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const openCorrectStatus = () => {
    setCorrectionStatus('');
    setCorrectionReason('');
    setIsCorrectingStatus(true);
  };

  const closeCorrectStatus = () => {
    setIsCorrectingStatus(false);
    setCorrectionStatus('');
    setCorrectionReason('');
  };

  // Status correction (task 60): a deliberate override, NOT a forward action.
  // On success we refetch so the corrected status + the new `status_corrected`
  // history entry both appear, and the queue tab badges refresh.
  const correctionMutation = useMutation(
    (payload: { status: TicketStatus; reason: string }) =>
      correctTicketStatus(orderId, {
        status: payload.status,
        reason: payload.reason,
      }),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries([QUERY_KEY, orderId]);
        queryClient.invalidateQueries('print-shop-tickets');
        queryClient.invalidateQueries('print-shop-ticket-counts');
        closeCorrectStatus();
        const message = formatMessage(
          printShopStrings.detail.correctStatus.success,
          {
            orderId,
            status:
              printShopStrings.statusLabels[updated.status] ?? updated.status,
          },
        );
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: (mutationError) => {
        const message = isStatusHeldError(mutationError)
          ? formatMessage(printShopStrings.detail.correctStatus.held, {
              orderId,
            })
          : getApiErrorMessage(
              mutationError,
              formatMessage(printShopStrings.detail.correctStatus.error, {
                orderId,
              }),
            );
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const isArchived = isArchivedTicket(ticket?.status);
  // Cancelled by an ABO operator: the archived view shows the cancellation
  // record, and the stepper stops at the status the order was cancelled from.
  const isOperatorCancelled = ticket
    ? isOperatorCancelledTicket(ticket)
    : false;
  const showCancelOutcome = (message: string) =>
    setBanner(nextBanner('SUCCESS', message));
  const isUnknownStatus = isUnknownStatusTicket(ticket?.status);
  // Waiting for the member's approval (book-order-approval): nothing in the
  // production flow may act on it yet — no hero action, no correction. The
  // approval panel carries the two things support can do instead.
  const isApprovalPending = isApprovalPendingStatus(ticket?.status);
  // Waiting for its Instant Trust Package (itp-mvp): the same guard. The ITP
  // card carries what the operator does instead.
  const isItpPending = isItpPendingStatus(ticket?.status);
  const isPrePrint = isPrePrintStatus(ticket?.status);
  /**
   * The member is gone. DS returns its own unknown-member placeholder rather
   * than omitting the field, so the marker is the placeholder itself.
   */
  const isFormerMember = ticket?.member?.email === 'unknown';

  // An imported record has no next action: the order was fulfilled years ago,
  // outside this system. Asking `actionForStatus` about it would be asking a
  // question about the work lane that this ticket was never in.
  const availableAction =
    ticket && !isArchived && !isPrePrint
      ? actionForStatus(ticket.status as TicketStatus)
      : null;

  const openConfirm = (action: TicketAction) => {
    setTrackingRef('');
    setConfirmEmptyShip(false);
    setPendingAction(action);
  };

  const confirmAction = () => {
    if (!pendingAction || statusMutation.isLoading) {
      return;
    }
    // Ship encourages a tracking ref: a blank one is allowed, but only after
    // an explicit "Ship without tracking?" step (never silently).
    if (
      pendingAction === 'ship' &&
      trackingRef.trim() === '' &&
      !confirmEmptyShip
    ) {
      setConfirmEmptyShip(true);
      return;
    }
    statusMutation.mutate(pendingAction);
  };

  const onCopyFullId = async () => {
    const ok = await copyToClipboard(orderId);
    if (ok) {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1500);
    }
  };

  // Changing the page size always re-queries from page 1; the indicator +
  // Prev/Next then paginate the rest at the chosen size (task 47).
  const changeRecipientPageSize = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRecipientPageSize(Number(event.target.value));
    setRecipientPage(1);
  };

  const startEditTracking = () => {
    setTrackingDraft(ticket?.trackingRef ?? '');
    setIsEditingTracking(true);
  };

  const cancelEditTracking = () => {
    setIsEditingTracking(false);
    setTrackingDraft('');
  };

  const saveTracking = () => {
    if (trackingDraft.trim() === '' || trackingMutation.isLoading) {
      return;
    }
    // No-op save: the draft matches the current ref (trimmed). Just close the
    // editor — skip the request so we don't write a pointless `tracking_updated`
    // audit row for an unchanged value.
    if (trackingDraft.trim() === (ticket?.trackingRef ?? '').trim()) {
      setIsEditingTracking(false);
      return;
    }
    trackingMutation.mutate(trackingDraft);
  };

  const saveCorrection = () => {
    if (
      correctionStatus === '' ||
      correctionStatus === ticket?.status ||
      correctionMutation.isLoading
    ) {
      return;
    }
    correctionMutation.mutate({
      status: correctionStatus,
      reason: correctionReason,
    });
  };

  // The correctable lane minus the ticket's current status (no no-op target).
  const correctableStatuses = CORRECTABLE_STATUSES.filter(
    (status) => status !== ticket?.status,
  );

  const recipients = recipientsPage?.data ?? [];
  const recipientsLastPage = Math.max(1, recipientsPage?.meta?.lastPage ?? 1);
  // The order includes a personalized cover letter → surface the per-recipient
  // letter column + the "Download all letters" bundle (tasks 2–4, 6).
  const hasCoverLetter = Boolean(ticket?.coverLetter);
  // A representative example for the print-files section: the first recipient
  // whose personalized letter has already rendered. Undefined until one is
  // ready, in which case the card shows a placeholder instead of a preview.
  const exampleCoverLetterUrl = recipients.find(
    (recipient) =>
      recipient.coverLetterStatus === 'rendered' && recipient.coverLetterPdfUrl,
  )?.coverLetterPdfUrl;
  // Rendered client-side via pdf.js rather than an <iframe src={pdfUrl}>: an
  // iframe shows the browser's own PDF-viewer chrome (print/download/rotate
  // toolbar) even scaled down to thumbnail size, which reads as a UI bug.
  const exampleCoverLetterThumbnail = usePdfFirstPageThumbnail(
    exampleCoverLetterUrl,
  );
  const isShipConfirm = pendingAction === 'ship' && confirmEmptyShip;

  // History collapses to the most recent events by default (task 57). The full
  // audit set is preserved — only hidden — and surfaced via the "Show all"
  // toggle. The DS already orders events oldest-first, so we reverse a copy to
  // show newest-first, then slice the leading window when collapsed.
  const events = ticket?.events ?? [];
  const orderedEvents = [...events].reverse();
  const hasHistoryToggle = orderedEvents.length > HISTORY_COLLAPSED_COUNT;
  const visibleEvents =
    hasHistoryToggle && !historyExpanded
      ? orderedEvents.slice(0, HISTORY_COLLAPSED_COUNT)
      : orderedEvents;

  // Auto-recover from a stranded out-of-range page: if the set shrank (or the
  // tab has fewer pages) and the operator is past the last page, the Prev/Next
  // controls live inside the `recipients.length > 0` branch and vanish — there
  // is no in-page way back. Clamp the page down to the last valid one so the
  // controls reappear instead of trapping them on an empty page.
  useEffect(() => {
    setRecipientPage((current) =>
      current > recipientsLastPage ? recipientsLastPage : current,
    );
  }, [recipientsLastPage]);

  if (isLoading) {
    return (
      <div className="pt-8 min-h-screen w-full flex justify-center items-start">
        <div className="py-20 flex flex-col items-center gap-3">
          <Spinner size="lg" aria-label={printShopStrings.detail.loading} />
          <span className="text-neutral-500 text-sm">
            {printShopStrings.detail.loading}
          </span>
        </div>
      </div>
    );
  }

  const statusLabel = ticket
    ? printShopStrings.statusLabels[ticket.status] ?? ticket.status
    : '';
  const hasSeparateCover =
    ticket?.asset.kind === 'book' && Boolean(ticket.asset.coverPdfUrl);
  // Back goes to the ticket's own queue, read from the ticket rather than from
  // where the operator came from, so a deep link, a lookup result and a batch
  // row all return to the right page (abo-print-shop-menu-split ADR-001). A
  // ticket that did not load falls back to `/print-shop`, which redirects.
  const backPath = ticket
    ? queuePathForOrigin(originOf(ticket))
    : PRINT_SHOP_ROOT_PATH;
  const backLabel = ticket
    ? formatMessage(printShopStrings.detail.backTo, {
        queue: printShopStrings.queue.titles[originOf(ticket)],
      })
    : printShopStrings.detail.backToQueue;

  return (
    <div className="pt-8 min-h-screen w-full">
      <ToastContainer />
      <div className="mb-4">
        <Link
          to={backPath}
          data-testid="ticket-back-link"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-800"
        >
          <HiOutlineArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
      </div>

      {banner &&
        (banner.batchId ? (
          // task_06: the batch-conflict banner is a link to the batch it
          // already sits in, not the plain <Notification/> — the single
          // orderInOpenBatch string doubles as its clickable text.
          <div className="my-4">
            <Link
              to={`/print-shop/batches/${banner.batchId}`}
              data-testid="batch-conflict-link"
              className="inline-flex max-w-full items-center rounded-md bg-red-100 px-3 py-2.5 text-sm font-semibold text-red-600 underline decoration-red-300 hover:text-red-700"
            >
              {banner.message}
            </Link>
          </div>
        ) : (
          <Notification
            key={banner.id}
            type={banner.type}
            message={banner.message}
          />
        ))}

      {!ticket && isNotFound && (
        <Notification type="OTHER" message={printShopStrings.detail.notFound} />
      )}
      {!ticket && !isNotFound && error != null && (
        <Notification
          type="FAILURE"
          message={printShopStrings.detail.loadError}
        />
      )}

      {ticket && (
        <>
          {/* Hero: identity, status, primary action, and the status stepper. */}
          <section className="mb-6 rounded-2xl bg-gradient-to-br from-primary-700 to-primary-500 px-6 py-6 text-white shadow-md">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="min-w-0 break-all font-mono text-lg font-extrabold tracking-wide sm:text-xl lg:text-2xl">
                    {formatMessage(printShopStrings.detail.pageTitle, {
                      orderId,
                    })}
                  </h1>
                  <button
                    type="button"
                    data-testid="copy-full-order-id"
                    onClick={onCopyFullId}
                    className="inline-flex items-center gap-1 rounded-md border border-white/50 bg-white/10 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-white/20"
                  >
                    <HiOutlineClipboardCopy className="h-4 w-4" />
                    {idCopied
                      ? printShopStrings.detail.copiedId
                      : printShopStrings.detail.copyFullId}
                  </button>
                </div>
                {ticket.member && !isFormerMember && (
                  <p className="mt-1.5 text-sm text-white/90">
                    <span className="font-semibold">{ticket.member.name}</span>{' '}
                    ·{' '}
                    <a
                      href={`mailto:${ticket.member.email}`}
                      className="text-white underline hover:text-white/80"
                    >
                      {ticket.member.email}
                    </a>
                  </p>
                )}
                {isFormerMember && (
                  /* Roughly 70% of the imported archive. Stated rather than
                     left blank, and NOT rendered as a mailto — the address is
                     the resolver's "unknown" placeholder, not a real inbox,
                     and offering to email it would send a message to nobody. */
                  <p
                    data-testid="ticket-former-member"
                    className="mt-1.5 text-sm text-white/90"
                    title={printShopStrings.archive.formerMemberHint}
                  >
                    {printShopStrings.archive.formerMember}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-end gap-2.5">
                <span
                  data-testid="ticket-status-badge"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${heroStatusPillClass(
                    ticket.status,
                  )}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {statusLabel}
                </span>
                {isArchived ? (
                  // An imported record has nothing to advance and nothing to
                  // correct it toward. Showing either control would invite an
                  // operator to act on an order fulfilled years ago.
                  <span
                    data-testid="ticket-archived-badge"
                    className="inline-flex items-center rounded-lg border border-white/50 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90"
                  >
                    {isUnknownStatus
                      ? printShopStrings.archive.badge
                      : printShopStrings.archive.cancelledBadge}
                  </span>
                ) : isApprovalPending ? (
                  // Where the print action would be. The order has not been
                  // approved, so the print shop has nothing to do yet.
                  <span
                    data-testid="ticket-awaiting-approval"
                    className="inline-flex items-center rounded-lg border border-white/50 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90"
                  >
                    {ticket.status === 'changes_requested'
                      ? printShopStrings.detail.approval.heroChangesRequested
                      : printShopStrings.detail.approval.heroAwaiting}
                  </span>
                ) : isItpPending ? (
                  // The ITP is not ready, so the order cannot be accepted yet.
                  <span
                    data-testid="ticket-waiting-for-itp"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/50 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90"
                  >
                    <HiOutlineLockClosed aria-hidden className="h-4 w-4" />
                    {printShopStrings.detail.itp.heroWaiting}
                  </span>
                ) : availableAction ? (
                  <button
                    type="button"
                    disabled={statusMutation.isLoading}
                    onClick={() => openConfirm(availableAction)}
                    className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-bold text-primary-700 shadow-sm transition hover:bg-primary-50 disabled:opacity-60"
                  >
                    {printShopStrings.actionLabels[availableAction]}
                  </button>
                ) : (
                  <span
                    data-testid="ticket-no-actions"
                    className="inline-flex items-center rounded-lg border border-white/50 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90"
                  >
                    {formatMessage(printShopStrings.detail.actions.terminal, {
                      status: statusLabel,
                    })}
                  </span>
                )}
                {/* Error-recovery affordance (task 60): deliberately understated
                  vs the primary forward action — "correct", not "advance". */}
                <div className="flex items-center gap-3">
                  {!isArchived && !isPrePrint && (
                    <button
                      type="button"
                      data-testid="edit-status-button"
                      onClick={openCorrectStatus}
                      aria-label={
                        printShopStrings.detail.correctStatus.editAriaLabel
                      }
                      className="text-xs font-semibold text-white/80 underline transition hover:text-white"
                    >
                      {printShopStrings.detail.correctStatus.editButton}
                    </button>
                  )}
                  {/* Operator cancel of a book order: role-gated, on a live
                    (not imported) order in any status, Shipped included. Rare,
                    so a small link like "Edit status", not a primary button. */}
                  {isBookTicket(ticket) && (
                    <BookCancelAction
                      ticket={ticket}
                      ticketQueryKey={[QUERY_KEY, orderId]}
                      onCancelled={showCancelOutcome}
                    />
                  )}
                </div>
              </div>
            </div>

            {isArchived && (
              <div
                data-testid="ticket-archived-banner"
                className="mt-4 rounded-lg border border-white/40 bg-white/10 px-4 py-3 text-sm text-white"
              >
                {/* A cancelled order and one whose status could not be
                    recovered are opposites: we know exactly how the first
                    ended and nothing about the second. Saying "no fulfilment
                    history was recoverable" over a cancelled order would tell
                    an operator we do not know something we do. */}
                {isOperatorCancelled ? (
                  <BookCancellationSummary
                    ticket={ticket}
                    ticketQueryKey={[QUERY_KEY, orderId]}
                    onSettled={showCancelOutcome}
                  />
                ) : (
                  <p className="font-semibold">
                    {isUnknownStatus
                      ? printShopStrings.archive.banner
                      : printShopStrings.archive.cancelledBanner}
                  </p>
                )}
                {isUnknownStatus && (
                  <p className="mt-1 text-white/80">
                    {printShopStrings.archive.bannerHint}
                  </p>
                )}
              </div>
            )}

            <ol
              className="mt-5 flex items-center overflow-x-auto"
              data-testid="ticket-stepper"
            >
              {(isOperatorCancelled
                ? cancelledStepStates(
                    ticket.cancellation?.fromStatus,
                    Boolean(ticket.itp),
                  )
                : ticketStepStates(
                    // The stepper draws the production lane. An imported
                    // record was never in it, so it is rendered at the
                    // terminal milestone rather than shown part-way through
                    // a journey it never took.
                    (isArchived ? 'shipped' : ticket.status) as TicketStatus,
                    // An ITP order waits for its ITP before Paid.
                    Boolean(ticket.itp),
                  )
              ).map(({ step, state }, index) => {
                const reached = state !== 'todo';
                return (
                  <React.Fragment key={step}>
                    {index > 0 && (
                      <span
                        className={`mx-2 h-0.5 min-w-[16px] flex-1 ${
                          reached ? 'bg-white' : 'bg-white/30'
                        }`}
                      />
                    )}
                    <li
                      className="flex shrink-0 items-center gap-2 text-[11px] font-semibold"
                      data-testid={`ticket-step-${step}`}
                      data-state={state}
                    >
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                          reached
                            ? 'bg-white text-primary-700'
                            : 'bg-white/25 text-white/70'
                        }`}
                      >
                        {state === 'done'
                          ? '✓'
                          : state === 'current'
                            ? '●'
                            : state === 'cancelled'
                              ? '✕'
                              : ''}
                      </span>
                      <span
                        className={
                          state === 'current' || state === 'cancelled'
                            ? 'font-bold'
                            : 'text-white/90'
                        }
                      >
                        {printShopStrings.detail.stepper[step]}
                      </span>
                    </li>
                  </React.Fragment>
                );
              })}
            </ol>
          </section>

          {isBookTicket(ticket) && (
            <BookOrderFacts
              ticket={ticket}
              onShipToChanged={() => {
                queryClient.invalidateQueries([QUERY_KEY, orderId]);
                // DS also rewrites the book's recipient row, which the
                // shipment table reads.
                queryClient.invalidateQueries([RECIPIENTS_QUERY_KEY, orderId]);
              }}
            />
          )}

          {isBookTicket(ticket) && ticket.approval && (
            <BookApprovalPanel ticket={ticket} queryKey={[QUERY_KEY, orderId]} />
          )}

          {isBookTicket(ticket) && ticket.itp && (
            <BookItpPanel ticket={ticket} queryKey={[QUERY_KEY, orderId]} />
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/*
              A book order's PDFs live in a private bucket and are served as
              short-lived signed URLs, so it gets its own panel rather than the
              direct-link cards below, which assume a reachable asset URL.
            */}
            {isBookTicket(ticket) ? (
              <BookPrintFiles
                ticket={ticket}
                onFilesChanged={() =>
                  queryClient.invalidateQueries([QUERY_KEY, orderId])
                }
              />
            ) : (
            /* Print files (task 41: cover + manuscript on separate machines). */
            <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm lg:col-span-2">
              <h2
                data-testid="print-files-heading"
                className="text-xs font-bold uppercase tracking-wider text-neutral-400"
              >
                {printShopStrings.detail.asset.filesSectionTitle} —{' '}
                {printShopStrings.assetKindLabels[ticket.asset.kind]}
              </h2>
              <p className="mb-4 mt-1 text-sm font-semibold text-neutral-800">
                {ticket.asset.title}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                {hasSeparateCover ? (
                  <>
                    <PrintFileCard
                      badge={printShopStrings.detail.asset.fileBadgeCover}
                      title={printShopStrings.detail.asset.coverTitle}
                      href={ticket.asset.coverPdfUrl as string}
                      thumbnailUrl={ticket.asset.thumbnailUrl}
                      downloadTestId="ticket-download-cover"
                      downloadLabel={printShopStrings.detail.asset.download}
                      downloadAriaLabel={
                        printShopStrings.detail.asset.downloadCover
                      }
                      previewTestId="ticket-preview-cover"
                      previewAriaLabel={
                        printShopStrings.detail.asset.previewCover
                      }
                    />
                    <PrintFileCard
                      badge={printShopStrings.detail.asset.fileBadgeManuscript}
                      title={printShopStrings.detail.asset.manuscriptTitle}
                      href={ticket.asset.pdfUrl}
                      renderThumbnailFromPdf
                      downloadTestId="ticket-download-manuscript"
                      downloadLabel={printShopStrings.detail.asset.download}
                      downloadAriaLabel={
                        printShopStrings.detail.asset.downloadManuscript
                      }
                      previewTestId="ticket-preview-manuscript"
                      previewAriaLabel={
                        printShopStrings.detail.asset.previewManuscript
                      }
                    />
                  </>
                ) : (
                  <PrintFileCard
                    badge={printShopStrings.detail.asset.fileBadgePdf}
                    href={ticket.asset.pdfUrl}
                    thumbnailUrl={ticket.asset.thumbnailUrl}
                    downloadTestId="ticket-download-pdf"
                    downloadLabel={
                      ticket.asset.kind === 'book'
                        ? printShopStrings.detail.asset.downloadBookPdf
                        : printShopStrings.detail.asset.downloadPdf
                    }
                    downloadAriaLabel={
                      ticket.asset.kind === 'book'
                        ? printShopStrings.detail.asset.downloadBookPdf
                        : printShopStrings.detail.asset.downloadPdf
                    }
                    previewTestId="ticket-preview-pdf"
                    previewAriaLabel={
                      ticket.asset.kind === 'book'
                        ? printShopStrings.detail.asset.previewBookPdf
                        : printShopStrings.detail.asset.previewPdf
                    }
                  />
                )}
              </div>
              {hasSeparateCover && (
                <p className="mt-3 text-[11px] text-neutral-400">
                  {printShopStrings.detail.asset.twoFileNote}
                </p>
              )}
              {/* Cover-letter example (book add-on): a representative preview of
                the personalized letter, pulled from the first rendered
                recipient. Personalized per recipient, so it's only ever an
                example — a placeholder shows until the first one renders. */}
              {ticket.coverLetter && (
                <div
                  data-testid="ticket-cover-letter-example"
                  className="mt-4 flex items-start gap-4 rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-4"
                >
                  <div className="aspect-[0.77] w-[120px] shrink-0 overflow-hidden rounded-md border border-primary-100 bg-white">
                    {exampleCoverLetterThumbnail.src ? (
                      <img
                        src={exampleCoverLetterThumbnail.src}
                        alt=""
                        data-testid="cover-letter-example-thumbnail"
                        className="h-full w-full object-cover"
                      />
                    ) : exampleCoverLetterUrl &&
                      !exampleCoverLetterThumbnail.failed ? (
                      <div className="h-full w-full animate-pulse bg-primary-100" />
                    ) : (
                      <span className="flex h-full items-center justify-center p-2 text-center text-[11px] text-neutral-400">
                        {
                          printShopStrings.detail.asset
                            .coverLetterExamplePlaceholder
                        }
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <h3 className="text-sm font-semibold text-neutral-800">
                      {printShopStrings.detail.asset.coverLetterExampleTitle} ·{' '}
                      {printShopStrings.detail.asset.coverLetterTemplateLabels[
                        ticket.coverLetter.templateKey
                      ] ?? ticket.coverLetter.templateKey}
                    </h3>
                    {exampleCoverLetterUrl && (
                      <a
                        href={exampleCoverLetterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="cover-letter-example-preview"
                        aria-label={
                          printShopStrings.detail.asset
                            .coverLetterExamplePreviewAria
                        }
                        className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-1.5 text-xs font-bold text-primary-700 transition hover:bg-primary-50"
                      >
                        <HiOutlineExternalLink className="h-4 w-4" />
                        {printShopStrings.detail.asset.preview}
                      </a>
                    )}
                    <span className="text-[11px] text-neutral-400">
                      {printShopStrings.detail.asset.coverLetterExampleNote}
                    </span>
                  </div>
                </div>
              )}
            </section>
            )}

            {/* Compact payment + audit history. */}
            <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-400">
                {printShopStrings.detail.payment.sectionTitle}
              </h2>
              <dl className="flex flex-col text-sm">
                <div className="flex justify-between border-b border-neutral-100 py-2">
                  <dt className="text-neutral-500">
                    {printShopStrings.detail.payment.paidAt}
                  </dt>
                  <dd className="font-semibold text-neutral-800">
                    {formatDateTime(ticket.paidAt)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-neutral-100 py-2">
                  <dt className="text-neutral-500">
                    {printShopStrings.detail.payment.creditsCharged}
                  </dt>
                  <dd className="font-semibold text-neutral-800">
                    {ticket.creditsCharged}
                  </dd>
                </div>
                <div className="flex flex-col gap-2 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-neutral-500">
                      {printShopStrings.detail.shipping.trackingRef}
                    </dt>
                    {!isEditingTracking && (
                      <div className="flex items-center gap-2">
                        <dd className="font-semibold text-neutral-800">
                          {ticket.trackingRef ??
                            printShopStrings.detail.shipping.notShipped}
                        </dd>
                        {/* Nothing has shipped before Paid (approval or ITP lanes). */}
                        {!isPrePrint && (
                          <button
                            type="button"
                            data-testid="edit-tracking-button"
                            onClick={startEditTracking}
                            aria-label={
                              printShopStrings.detail.shipping.editTrackingAriaLabel
                            }
                            className="text-xs font-semibold text-primary-700 hover:underline"
                          >
                            {printShopStrings.detail.shipping.editTracking}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* A MOD order ships one asset to N recipients via a batch
                    method, so this single value is a batch/shipment reference,
                    not per-parcel tracking — set that expectation (task 55). */}
                  <span
                    data-testid="not-individually-tracked"
                    className="text-xs text-neutral-400"
                  >
                    {
                      printShopStrings.detail.shipping
                        .notIndividuallyTracked
                    }
                  </span>
                  {isEditingTracking && (
                    <div className="flex flex-col gap-2">
                      <TextInput
                        id="edit-tracking-ref"
                        data-testid="edit-tracking-input"
                        value={trackingDraft}
                        placeholder={
                          printShopStrings.detail.shipping.trackingPlaceholder
                        }
                        aria-label={
                          printShopStrings.detail.shipping.trackingInputLabel
                        }
                        onChange={(event) =>
                          setTrackingDraft(event.target.value)
                        }
                      />
                      {trackingDraft.trim() === '' && (
                        <span
                          data-testid="edit-tracking-required"
                          className="text-xs text-neutral-400"
                        >
                          {printShopStrings.detail.shipping.trackingRequired}
                        </span>
                      )}
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="OUTLINED"
                          disabled={trackingMutation.isLoading}
                          onClick={cancelEditTracking}
                        >
                          {printShopStrings.detail.shipping.cancelTracking}
                        </Button>
                        <Button
                          variant="PRIMARY"
                          isProcessing={trackingMutation.isLoading}
                          disabled={
                            trackingDraft.trim() === '' ||
                            trackingMutation.isLoading
                          }
                          onClick={saveTracking}
                        >
                          {printShopStrings.detail.shipping.saveTracking}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </dl>

              <h2 className="mb-2 mt-4 text-xs font-bold uppercase tracking-wider text-neutral-400">
                {printShopStrings.detail.history.sectionTitle}
              </h2>
              {events.length === 0 ? (
                <p className="text-sm text-neutral-500">
                  {printShopStrings.detail.history.empty}
                </p>
              ) : (
                <>
                  <ol
                    className="flex flex-col gap-2 text-[13px]"
                    data-testid="ticket-history"
                  >
                    {visibleEvents.map((event, index) => (
                      <li
                        key={`${event.createdAt}-${index}`}
                        className="flex flex-col gap-0.5 border-l-2 border-neutral-100 pl-3"
                      >
                        <span className="font-semibold text-neutral-800">
                          {historyEventLabel(event)}
                        </span>
                        <span className="text-xs text-neutral-500">
                          {formatMessage(printShopStrings.detail.history.actor, {
                            actor: `${event.actor.type}:${event.actor.id}`,
                          })}{' '}
                          · {formatDateTime(event.createdAt)}
                        </span>
                      </li>
                    ))}
                  </ol>
                  {hasHistoryToggle && (
                    <button
                      type="button"
                      data-testid="ticket-history-toggle"
                      aria-expanded={historyExpanded}
                      onClick={() => setHistoryExpanded((current) => !current)}
                      className="mt-2 text-xs font-semibold text-primary-700 hover:underline"
                    >
                      {historyExpanded
                        ? printShopStrings.detail.history.showLess
                        : formatMessage(
                            printShopStrings.detail.history.showAll,
                            { count: String(orderedEvents.length) },
                          )}
                    </button>
                  )}
                </>
              )}
            </section>

            {/* Recipients: the mailing set only, paginated, CSV export (task 56). */}
            <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm lg:col-span-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  {/*
                    A book order ships N copies to ONE address and has no
                    audience, so the mailing wording does not apply — and
                    `quantity` there is the COPY count, which read as
                    "24 recipients" on a ticket with a single row.
                  */}
                  {isBookTicket(ticket)
                    ? printShopStrings.book.shipmentTitle
                    : printShopStrings.detail.recipients.sectionTitle}{' '}
                  <span className="font-medium normal-case tracking-normal text-neutral-400">
                    ·{' '}
                    {isBookTicket(ticket)
                      ? printShopStrings.book.shipmentSummary(ticket.quantity)
                      : formatMessage(
                          printShopStrings.detail.recipients.summary,
                          { mailable: String(ticket.quantity) },
                        )}
                  </span>
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  {hasCoverLetter && (
                    <Button
                      variant="PRIMARY"
                      isProcessing={lettersMutation.isLoading}
                      disabled={lettersMutation.isLoading}
                      onClick={() => lettersMutation.mutate()}
                    >
                      <HiOutlineDownload className="mr-2 h-4 w-4" />
                      {printShopStrings.detail.recipients.downloadAllLetters}
                    </Button>
                  )}
                  <Button
                    variant="OUTLINED"
                    isProcessing={csvMutation.isLoading}
                    disabled={csvMutation.isLoading}
                    onClick={() => csvMutation.mutate()}
                  >
                    <HiOutlineDownload className="mr-2 h-4 w-4" />
                    {printShopStrings.detail.recipients.downloadCsv}
                  </Button>
                </div>
              </div>

              {recipientsError ? (
                <Notification
                  type="FAILURE"
                  message={printShopStrings.detail.recipients.loadError}
                />
              ) : recipientsLoading ? (
                <div className="py-6 flex justify-center">
                  <Spinner aria-label={printShopStrings.detail.loading} />
                </div>
              ) : recipients.length === 0 ? (
                <p className="py-6 text-sm text-neutral-500">
                  {printShopStrings.detail.recipients.tableEmpty}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="recipients-table">
                    <Table.Head>
                      <Table.HeadCell className="px-4 py-2">
                        {printShopStrings.detail.recipients.columnName}
                      </Table.HeadCell>
                      <Table.HeadCell className="px-4 py-2">
                        {printShopStrings.detail.recipients.columnAddress}
                      </Table.HeadCell>
                      {hasCoverLetter && (
                        <Table.HeadCell className="px-4 py-2">
                          {printShopStrings.detail.recipients.coverLetterColumn}
                        </Table.HeadCell>
                      )}
                      <Table.HeadCell className="px-4 py-2 text-right">
                        {printShopStrings.detail.recipients.columnStatus}
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {recipients.map((recipient) => (
                        <Table.Row
                          key={recipient.id}
                          data-testid={`recipient-row-${recipient.id}`}
                        >
                          <Table.Cell className="px-4 py-2 font-medium text-neutral-800">
                            {recipientName(recipient)}
                          </Table.Cell>
                          <Table.Cell className="px-4 py-2 text-neutral-600">
                            {formatRecipientAddress(recipient)}
                          </Table.Cell>
                          {hasCoverLetter && (
                            <Table.Cell className="px-4 py-2">
                              <RecipientCoverLetterCell
                                recipient={recipient}
                                onRetry={(recipientId) =>
                                  retryCoverLetterMutation.mutate(recipientId)
                                }
                                isRetrying={
                                  retryCoverLetterMutation.isLoading &&
                                  retryCoverLetterMutation.variables ===
                                    recipient.id
                                }
                              />
                            </Table.Cell>
                          )}
                          <Table.Cell className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {recipient.overridden && (
                                <Badge
                                  color="info"
                                  className="w-fit"
                                  title={formatMessage(
                                    printShopStrings.detail.recipients
                                      .correctedOriginal,
                                    {
                                      address: recipient.originalAddress
                                        ? formatRecipientAddress(
                                            recipient.originalAddress,
                                          )
                                        : printShopStrings.queue.noTracking,
                                    },
                                  )}
                                >
                                  {
                                    printShopStrings.detail.recipients
                                      .correctedBadge
                                  }
                                </Badge>
                              )}
                              {/* The card shows the mailing set only (task 56),
                                so every row is on the mailing list. */}
                              <Badge color="success" className="w-fit">
                                {printShopStrings.detail.recipients.mailingBadge}
                              </Badge>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-neutral-500">
                    <label className="flex items-center gap-2">
                      <span>
                        {printShopStrings.detail.recipients.perPageLabel}
                      </span>
                      <select
                        className="rounded-md border border-neutral-300 px-2 py-1 text-xs"
                        value={recipientPageSize}
                        onChange={changeRecipientPageSize}
                        data-testid="recipients-pagesize"
                        aria-label={
                          printShopStrings.detail.recipients.pageSizeAriaLabel
                        }
                      >
                        {RECIPIENTS_PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>

                    {recipientsLastPage > 1 && (
                      <div className="flex items-center gap-3">
                        <Button
                          variant="OUTLINED"
                          disabled={recipientPage <= 1}
                          onClick={() =>
                            setRecipientPage((current) =>
                              Math.max(1, current - 1),
                            )
                          }
                        >
                          {printShopStrings.queue.prev}
                        </Button>
                        <span data-testid="recipients-page-indicator">
                          {formatMessage(printShopStrings.queue.pageIndicator, {
                            currentPage: String(recipientPage),
                            lastPage: String(recipientsLastPage),
                          })}
                        </span>
                        <Button
                          variant="OUTLINED"
                          disabled={recipientPage >= recipientsLastPage}
                          onClick={() =>
                            setRecipientPage((current) => current + 1)
                          }
                        >
                          {printShopStrings.queue.next}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}

      <DefaultModal
        title={
          isShipConfirm
            ? printShopStrings.detail.actions.shipNoTrackingTitle
            : printShopStrings.detail.actions.confirmTitle
        }
        showModal={pendingAction !== null}
        setShowModal={(show) => {
          if (!show) {
            setPendingAction(null);
            setConfirmEmptyShip(false);
          }
        }}
        size="md"
      >
        {pendingAction && ticket && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-neutral-800">
              {isShipConfirm
                ? formatMessage(
                    printShopStrings.detail.actions.shipNoTrackingMessage,
                    { orderId },
                  )
                : formatMessage(printShopStrings.detail.actions.confirmMessage, {
                    orderId,
                    from: printShopStrings.statusLabels[ticket.status],
                    to: printShopStrings.statusLabels[
                      ACTION_RESULT_STATUS[pendingAction]
                    ],
                  })}
            </p>
            {pendingAction === 'ship' && !confirmEmptyShip && (
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="ship-tracking-ref"
                  className="text-sm text-neutral-800 font-semibold"
                >
                  {printShopStrings.detail.actions.shipTrackingLabel}
                </label>
                <TextInput
                  id="ship-tracking-ref"
                  data-testid="ship-tracking-ref"
                  value={trackingRef}
                  placeholder={
                    printShopStrings.detail.actions.shipTrackingPlaceholder
                  }
                  onChange={(event) => setTrackingRef(event.target.value)}
                />
                <span className="text-xs text-neutral-400">
                  {printShopStrings.detail.actions.shipTrackingHint}
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="OUTLINED"
                onClick={() => {
                  setPendingAction(null);
                  setConfirmEmptyShip(false);
                }}
              >
                {printShopStrings.detail.actions.cancelButton}
              </Button>
              <Button
                variant="PRIMARY"
                isProcessing={statusMutation.isLoading}
                disabled={statusMutation.isLoading}
                onClick={confirmAction}
              >
                {isShipConfirm
                  ? printShopStrings.detail.actions.shipNoTrackingConfirm
                  : printShopStrings.detail.actions.confirmButton}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>

      {/* Status correction (task 60): override the status to fix a mistake. */}
      <DefaultModal
        title={printShopStrings.detail.correctStatus.dialogTitle}
        showModal={isCorrectingStatus}
        setShowModal={(show) => {
          if (!show) {
            closeCorrectStatus();
          }
        }}
        size="md"
      >
        {ticket && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-neutral-600">
              {printShopStrings.detail.correctStatus.intro}
            </p>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="correct-status-select"
                className="text-sm font-semibold text-neutral-800"
              >
                {printShopStrings.detail.correctStatus.statusLabel}
              </label>
              <select
                id="correct-status-select"
                data-testid="correct-status-select"
                value={correctionStatus}
                onChange={(event) =>
                  setCorrectionStatus(event.target.value as TicketStatus)
                }
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              >
                <option value="" disabled>
                  {printShopStrings.detail.correctStatus.statusPlaceholder}
                </option>
                {correctableStatuses.map((status) => (
                  <option key={status} value={status}>
                    {printShopStrings.statusLabels[status] ?? status}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="correct-status-reason"
                className="text-sm font-semibold text-neutral-800"
              >
                {printShopStrings.detail.correctStatus.reasonLabel}
              </label>
              <TextInput
                id="correct-status-reason"
                data-testid="correct-status-reason"
                value={correctionReason}
                placeholder={
                  printShopStrings.detail.correctStatus.reasonPlaceholder
                }
                onChange={(event) => setCorrectionReason(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="OUTLINED"
                disabled={correctionMutation.isLoading}
                onClick={closeCorrectStatus}
              >
                {printShopStrings.detail.correctStatus.cancelButton}
              </Button>
              <Button
                variant="PRIMARY"
                isProcessing={correctionMutation.isLoading}
                disabled={
                  correctionStatus === '' ||
                  correctionStatus === ticket.status ||
                  correctionMutation.isLoading
                }
                onClick={saveCorrection}
              >
                {printShopStrings.detail.correctStatus.confirmButton}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>
    </div>
  );
}
